import {
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { queriesWorkLogs } from "@query";
import {
  routeHelpersWorkLogPayeeDetails,
  routeHelpersWorkLogPayerDetails
} from "../paths";
import { getWorkLogPayeeStatusCellProps } from "../utils/getWorkLogPayeeStatusCellProps";
import { getWorkLogPayerStatusCellProps } from "../utils/getWorkLogPayerStatusCellProps";

type Props = {
  workLog?: WorkLog;
  shimmer?: boolean;
  /** Decides both the status wording and which detail URL a row links to. */
  currentUserRole?: "payer" | "payee";
  /**
   * The worker whose other work logs these are. Payer-only: `listWorkLogs` is
   * already scoped to the payee's own account on the payee side, but a payer's
   * read spans everyone they pay, so it has to be narrowed to one person.
   * Read off the payee search row the detail page already loads.
   */
  payeeAccountId?: string | null;
};

/**
 * The other work logs this worker filed against the same pay period.
 *
 * A worker files one work log per work site/engagement, so a pay period
 * routinely holds several for the same person — and each is approved and paid
 * separately. They are also exactly the set one pay statement covers, which is
 * why cancelling that statement releases all of them. So the scope here is the
 * *worker* within the period, not the engagement: narrowing to the engagement
 * would return only the work log already on screen.
 */
export const WorkLogsInPayPeriod: React.FC<Props> = props => {
  const currentUserRole = props.currentUserRole ?? "payer";
  // Both are called unconditionally (hook rules); only one result is used.
  const goToPayerWorkLog = routeHelpersWorkLogPayerDetails.useGoTo();
  const goToPayeeWorkLog = routeHelpersWorkLogPayeeDetails.useGoTo();
  const goToWorkLog =
    currentUserRole === "payer" ? goToPayerWorkLog : goToPayeeWorkLog;

  const payPeriodId = props.workLog?.payPeriodId;
  const payeeAccountId = props.payeeAccountId ?? undefined;

  // A payer reads every work log they receive, so the period alone is not a
  // scope — it has to be narrowed to the one worker. A payee's read is already
  // scoped to their own account, and a pay period belongs to a single payer's
  // schedule, so the period alone is enough there.
  const isScoped = currentUserRole === "payee" || !!payeeAccountId;

  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQuery(
    {
      filter: {
        payPeriodId: { eq: payPeriodId! },
        ...(payeeAccountId ? { payeeAccountId: { eq: payeeAccountId } } : {})
      },
      page: { size: 50 }
    },
    { enabled: !!payPeriodId && isScoped }
  );

  if (props.shimmer || !payPeriodId || !isScoped) {
    return null;
  }

  const siblingWorkLogs = (queryWorkLogs.data?.data ?? []).filter(
    workLog => workLog.id !== props.workLog?.id
  );

  if (siblingWorkLogs.length === 0) {
    return null;
  }

  return (
    <WSElement>
      <WSSectionToolbar title="Other work logs in this pay period" />
      <WSCard>
        <WSList gap="NONE">
          {siblingWorkLogs.map((sibling, index) => {
            const { pill } =
              currentUserRole === "payer"
                ? getWorkLogPayerStatusCellProps({ workLog: sibling })
                : getWorkLogPayeeStatusCellProps({ workLog: sibling });

            return (
              <React.Fragment key={sibling.id ?? index}>
                {index > 0 && <WSDivider />}
                <WSFlexBox
                  justify="space-between"
                  alignItems="center"
                  py="M"
                  onClick={
                    sibling.id
                      ? () => goToWorkLog({ workLogId: sibling.id! })
                      : undefined
                  }
                >
                  <WSText.ParagraphSm color="gray700">
                    #{sibling.workLogNumber}
                  </WSText.ParagraphSm>

                  <WSFlexBox.CenterY gap="S">
                    {pill && <WSPill {...pill} />}
                    <WSIcon name="chevron-right" color="gray400" size="XS" />
                  </WSFlexBox.CenterY>
                </WSFlexBox>
              </React.Fragment>
            );
          })}
        </WSList>
      </WSCard>
    </WSElement>
  );
};
